// The IANA time zone could be America/New_York, but the other could be America/Detroit.
// Just using the string values, it would appear that these are different time zones, but they are actually the same.
// This function checks if the two time zones are equivalent in terms of offset a few dates in the same year.
// compareTimeZone defaults to the user's local time zone when not supplied.
export const isSameTimeZone = (
  timeZone: string,
  compareTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
) => {
  // Return early if the string values are the same
  if (timeZone === compareTimeZone) {
    return true
  }

  // Otherwise, check if the two time zones have the same offset for a few dates in the same year.
  const dates = [0, 3, 6, 9].map((month) => new Date(Date.UTC(new Date().getFullYear(), month, 1, 12)))

  const getOffset = (tz: string, date: Date) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'longOffset',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value

  return dates.every((date) => getOffset(timeZone, date) === getOffset(compareTimeZone, date))
}

// Return short time zone name for a given IANA time zone.
export const shortTimeZone = (timeZone: string) => {
  // Some hs tz are GMT+/- so we want to represent them the same way moment does for calendar purposes
  if (/^Etc\//.test(timeZone)) {
    const displayTimeZone = timeZone.replace(/^Etc\/GMT([+-]\d+)?$/, (_, offset = '') => {
      if (!offset) return 'GMT'

      return `GMT${offset.startsWith('+') ? '-' : '+'}${offset.slice(1)}`
    })
    return displayTimeZone
  }
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  )
}
